import { bool, func, oneOf, shape, string } from 'prop-types';
import {
  BOOKING_PROCESS_NAME,
  INQUIRY_PROCESS_NAME,
  PURCHASE_PROCESS_NAME,
  NEGOTIATION_PROCESS_NAME,
  getTransactionProcess,
  getTransactionProcessName,
} from '../../transactions/transaction';
import {
  isTransitionInNextTransitions,
  resolveTransitionNameFromNextTransitions,
  toCanonicalTransitionName,
  transitionNamesMatch,
} from '../../transactions/transitionAliases';
import { getStateDataForBookingProcess } from './TransactionPage.stateDataBooking.js';
import { getStateDataForInquiryProcess } from './TransactionPage.stateDataInquiry.js';
import { getStateDataForPurchaseProcess } from './TransactionPage.stateDataPurchase.js';
import { getStateDataForNegotiationProcess } from './TransactionPage.stateDataNegotiation.js';
import {
  CANONICAL_STATUSES,
  getCanonicalStatus,
  getStatusLabel,
} from '../../util/transactionStatusMap';
import {
  FRONTEND_CANCELED_STATUS_LABEL,
  STUCK_LIVE_RECEIVED_STATUS_LABEL,
  isFrontendCanceledTransaction,
  isLegacyPayPalReceivedTransaction,
  isStuckLiveReceivedTransaction,
} from '../../util/transactionProductionOverrides';

const errorShape = shape({
  type: oneOf(['error']).isRequired,
  name: string.isRequired,
  message: string,
});

const actionButtonsShape = shape({
  inProgress: bool,
  error: errorShape,
  onAction: func.isRequired,
  buttonText: string,
  errorText: string,
});

export const stateDataShape = shape({
  processName: string.isRequired,
  processState: string.isRequired,
  primaryButtonProps: actionButtonsShape,
  secondaryButtonProps: actionButtonsShape,
  tertiaryButtonProps: actionButtonsShape,
  showActionButtons: bool,
  showDetailCardHeadings: bool,
  showDispute: bool,
  showOrderPanel: bool,
  showReviewAsFirstLink: bool,
  showReviewAsSecondLink: bool,
  showReviews: bool,
  statusTitle: string,
});

// Transitions are following process.edn format: "transition/my-transtion-name"
// This extracts the 'my-transtion-name' string if namespace exists
const getTransitionKey = transitionName => {
  const canonicalTransition = toCanonicalTransitionName(transitionName);
  const [nameSpace, transitionKey] = canonicalTransition.split('/');
  return transitionKey || transitionName;
};

// Action button prop for the TransactionPanel
const getActionButtonPropsMaybe = (params, onlyForRole = 'both') => {
  const {
    processName,
    transitionName,
    inProgress,
    transitionError,
    onAction,
    transactionRole,
    actionButtonTranslationId,
    actionButtonTranslationErrorId,
    intl,
    nextTransitions,
    ...extraParams
  } = params;
  const resolvedTransitionName = resolveTransitionNameFromNextTransitions(
    transitionName,
    nextTransitions
  );
  const transitionKey = getTransitionKey(transitionName);

  const actionButtonTrId =
    actionButtonTranslationId ||
    `TransactionPage.${processName}.${transactionRole}.transition-${transitionKey}.actionButton`;

  const isPastNegotiationOffersInvalidError =
    transitionError?.statusText === 'Past negotiation offers are invalid';
  const actionButtonTrErrorId = isPastNegotiationOffersInvalidError
    ? `TransactionPage.${processName}.actionError.pastNegotiationOffersInvalid`
    : actionButtonTranslationErrorId ||
      `TransactionPage.${processName}.${transactionRole}.transition-${transitionKey}.actionError`;

  const isProcessTransition =
    typeof transitionName === 'string' && transitionName.startsWith('transition/');
  const hasNextTransitions = Array.isArray(nextTransitions) && nextTransitions.length > 0;
  const shouldShowButton =
    !isProcessTransition ||
    !hasNextTransitions ||
    isTransitionInNextTransitions(transitionName, nextTransitions);

  return onlyForRole === 'both' || onlyForRole === transactionRole
    ? shouldShowButton
      ? {
          inProgress,
          error: transitionError,
          onAction: onAction || (transitionName ? () => {} : undefined),
          buttonText: intl.formatMessage({ id: actionButtonTrId }),
          errorText: intl.formatMessage({ id: actionButtonTrErrorId }),
          resolvedTransitionName,
          ...extraParams,
        }
      : {}
    : {};
};

export const getStateData = params => {
  const {
    transaction,
    transactionRole,
    intl,
    transitionInProgress,
    transitionError,
    onTransition,
    nextTransitions,
    sendReviewInProgress,
    sendReviewError,
    onOpenReviewModal,
    //onOpenRequestChangesModal,
    //onOpenMakeCounterOfferModal,
    //onCheckoutRedirect,
    //onMakeOfferRedirect,
  } = params;
  const isCustomer = transactionRole === 'customer';
  const processName = getTransactionProcessName(transaction, { nextTransitions });
  const process = getTransactionProcess(transaction, { nextTransitions });

  if (!processName || !process) {
    return {};
  }

  const getActionButtonProps = (transitionName, forRole, extra = {}) => {
    const { orderData, ...rest } = extra;
    const params = orderData ? { orderData } : {};
    const resolvedTransitionName = resolveTransitionNameFromNextTransitions(
      transitionName,
      nextTransitions
    );

    const showButton =
      !transitionName ||
      !Array.isArray(nextTransitions) ||
      !nextTransitions.length ||
      isTransitionInNextTransitions(transitionName, nextTransitions);

    const inProgress = transitionNamesMatch(
      transitionInProgress,
      resolvedTransitionName || transitionName
    );

    return getActionButtonPropsMaybe(
      {
        processName,
        transitionName,
        transactionRole,
        intl,
        inProgress,
        transitionError,
        nextTransitions,
        onAction:
          showButton && resolvedTransitionName
            ? () => onTransition(transaction?.id, resolvedTransitionName, params)
            : null,
        ...rest,
      },
      forRole
    );
  };

  const getLeaveReviewProps = isCustomer
    ? getActionButtonPropsMaybe({
        processName,
        transitionName: 'leaveReview',
        transactionRole,
        intl,
        inProgress: sendReviewInProgress,
        transitionError: sendReviewError,
        onAction: onOpenReviewModal,
        actionButtonTranslationId: 'TransactionPage.leaveReview.actionButton',
        actionButtonTranslationErrorId: 'TransactionPage.leaveReview.actionError',
      })
    : {};

  const processInfo = () => {
    const { getState, states, transitions } = process;
    const processState = getState(transaction);
    return {
      processName,
      processState,
      states,
      transitions,
      isCustomer,
      actionButtonProps: getActionButtonProps,
      leaveReviewProps: getLeaveReviewProps,
    };
  };

  let resolvedStateData = {};

  if (processName === PURCHASE_PROCESS_NAME) {
    resolvedStateData = getStateDataForPurchaseProcess(params, processInfo());
  } else if (processName === BOOKING_PROCESS_NAME) {
    resolvedStateData = getStateDataForBookingProcess(params, processInfo());
  } else if (processName === INQUIRY_PROCESS_NAME) {
    resolvedStateData = getStateDataForInquiryProcess(params, processInfo());
  } else if (processName === NEGOTIATION_PROCESS_NAME) {
    resolvedStateData = getStateDataForNegotiationProcess(params, processInfo());
  } else {
    resolvedStateData = {};
  }

  const canonicalStatus = getCanonicalStatus(transaction, transactionRole);
  const statusLabel = getStatusLabel({ transaction, viewerRole: transactionRole, intl });

  const baseStateData = {
    ...resolvedStateData,
    canonicalStatus,
    statusLabel,
  };

  if (isFrontendCanceledTransaction(transaction)) {
    // Temporary frontend-only fallback for one live transaction that cannot be canceled
    // through backend transitions. Suppress all active transaction CTAs in detail views.
    return {
      ...baseStateData,
      canonicalStatus: CANONICAL_STATUSES.CANCELED,
      statusLabel: FRONTEND_CANCELED_STATUS_LABEL,
      statusTitle: FRONTEND_CANCELED_STATUS_LABEL,
      showActionButtons: false,
      showDetailCardHeadings: true,
      showDispute: false,
      showExtraInfo: false,
      showOrderPanel: false,
      showReviewAsFirstLink: false,
      showReviewAsSecondLink: false,
      isPendingPayment: false,
      primaryButtonProps: undefined,
      secondaryButtonProps: undefined,
      tertiaryButtonProps: undefined,
      actionButtonOrder: undefined,
    };
  }

  const isReceivedFallback =
    isStuckLiveReceivedTransaction(transaction) || isLegacyPayPalReceivedTransaction(transaction);

  if (!isReceivedFallback) {
    return baseStateData;
  }

  // Show legacy process-version fallbacks as Received and suppress every frontend CTA
  // that could attempt another transition against the immutable process.
  return {
    ...baseStateData,
    canonicalStatus: isCustomer ? CANONICAL_STATUSES.WAITING_REVIEW : CANONICAL_STATUSES.COMPLETED,
    statusLabel: STUCK_LIVE_RECEIVED_STATUS_LABEL,
    statusTitle: STUCK_LIVE_RECEIVED_STATUS_LABEL,
    showActionButtons: false,
    showDetailCardHeadings: true,
    showDispute: false,
    showExtraInfo: false,
    showOrderPanel: false,
    showReviewAsFirstLink: false,
    showReviewAsSecondLink: false,
    isPendingPayment: false,
    primaryButtonProps: undefined,
    secondaryButtonProps: undefined,
    tertiaryButtonProps: undefined,
    actionButtonOrder: undefined,
  };
};
